
import { Card, Deck } from "./cards";
import { getActionCardDefinition } from "./cardCatalog";
import { actionS1BaseImages, actionS1FlexImages, actionS2BaseImages, actionS2FlexImages, actionS3BaseImages, actionS3FlexImages, actionStarterImages, cellarActionS1Images, cellarActionS2Images, cellarActionS3Images, hiveActionS1Images, hiveActionS2Images, hiveActionS3Images, objectivesS1Images, objectivesS2Images, objectiveS3Images, oldCardImages } from "./images/cards/cardsLists";
import { getSeedEventDefinition, getSeedEventCardDefinition, isHiveInfluencerSeed } from "./seedEvents";

export interface DeckSetupOptions {
    hiveExpansionEnabled?: boolean;
    cellarExpansionEnabled?: boolean;
    includeAdvancedCards?: boolean;
    guaranteedActionCardNames?: string[];
    currentSeed?: string;
    campaignGameNumber?: 1 | 2 | 3;
}

export interface SerializedDeckState {
    deckActionCardDraw: { cards: { name: string }[] };
    deckActionCardDiscard: { cards: { name: string }[] };
    deckActionDrawLeft: { cards: { name: string }[] };
    deckActionDrawRight: { cards: { name: string }[] };
    deckActionCardActive: { cards: { name: string }[] };
    objectiveOne: { name: string } | null;
    objectiveTwo: { name: string } | null;
    objectiveThree: { name: string } | null;
}

const SEASON_VISIBLE_CARD_COUNTS = {
    1: 8,
    2: 8,
    3: 6
} as const;
// All image arrays from cardsLists.ts
const allImageArrays = [
    actionS1BaseImages, actionS1FlexImages, actionS2BaseImages, actionS2FlexImages, actionS3BaseImages, actionS3FlexImages, actionStarterImages, hiveActionS1Images, hiveActionS2Images, hiveActionS3Images, cellarActionS1Images, cellarActionS2Images, cellarActionS3Images, objectivesS1Images, objectivesS2Images, objectiveS3Images, oldCardImages
];

// Function to find card counterparts
const findCardInImageArrays = (cardName: string) => {
    for (let imageArray of allImageArrays) {
        const match = imageArray.find((imageImport: any) => imageImport.includes(cardName));
        if (match) {
            return match;
        }
    }
    return null;
};

export class DeckManager {


    public deckActionCardDraw: Deck;
    public deckActionCardDiscard: Deck;
    public deckActionCardActive: Deck;
    public deckActionDrawLeft: Deck;
    public deckActionDrawRight: Deck;

    public objectiveOne: Card | undefined;
    public objectiveTwo: Card | undefined;
    public objectiveThree: Card | undefined;
    private cardsWithFullscreenListeners: Set<string>;
    private fullscreenCloseListenerBound: boolean;

    constructor() {
        this.deckActionCardDraw = new Deck();
        this.deckActionCardDiscard = new Deck();
        this.deckActionDrawLeft = new Deck();
        this.deckActionDrawRight = new Deck();
        this.deckActionCardActive = new Deck();

        this.objectiveOne = undefined;
        this.objectiveTwo = undefined;
        this.objectiveThree = undefined;
        this.cardsWithFullscreenListeners = new Set();
        this.fullscreenCloseListenerBound = false;
    }

    public SetupFixedDecks(gameDataObject: any) {
        this.restoreSerializedState(gameDataObject);
    }

    private loadCardsIntoDeck(deck: Deck, gameDataCards: { name: string }[]) {
        deck = new Deck();
        const cardNames = gameDataCards.map((card) => card.name);

        for (const cardName of cardNames) {
            const card = this.createCardByName(cardName);

            if (card != null) {
                deck.addCard(card);
            }
        }

        console.log('create deck', deck);
        return deck;
    }

    public SetupDecks(options: DeckSetupOptions = {}): void {
        if (isHiveInfluencerSeed(options.currentSeed)) {
            options = { ...options, hiveExpansionEnabled: true, cellarExpansionEnabled: false, guaranteedActionCardNames: [] };
        }
        const includeAdvancedCards = options.includeAdvancedCards ?? true;
        const guaranteedActionCardNames = options.guaranteedActionCardNames ?? [];
        const useCampaignSetup = (options.cellarExpansionEnabled ?? false) || guaranteedActionCardNames.length > 0;

        this.resetDecks();
        this.setupObjectives(options.currentSeed, options.campaignGameNumber);

        if (!useCampaignSetup) {
            this.setupStandardDecks({
                hiveExpansionEnabled: options.hiveExpansionEnabled,
                includeAdvancedCards,
                currentSeed: options.currentSeed
            });
            return;
        }

        this.setupCampaignDecks({
            includeAdvancedCards,
            guaranteedActionCardNames,
            cellarExpansionEnabled: options.cellarExpansionEnabled,
            currentSeed: options.currentSeed
        });
    }

    public getSerializedState(): SerializedDeckState {
        return {
            deckActionCardDraw: this.deckActionCardDraw.serialize(),
            deckActionCardDiscard: this.deckActionCardDiscard.serialize(),
            deckActionDrawLeft: this.deckActionDrawLeft.serialize(),
            deckActionDrawRight: this.deckActionDrawRight.serialize(),
            deckActionCardActive: this.deckActionCardActive.serialize(),
            objectiveOne: this.objectiveOne ? this.objectiveOne.serialize() : null,
            objectiveTwo: this.objectiveTwo ? this.objectiveTwo.serialize() : null,
            objectiveThree: this.objectiveThree ? this.objectiveThree.serialize() : null,
        };
    }

    public getCurrentGameActionCardNames(options: { excludeStarterCards?: boolean } = {}): string[] {
        const excludeStarterCards = options.excludeStarterCards ?? false;
        const seenNames = new Set<string>();
        const orderedNames: string[] = [];
        const pushCardName = (card: Card | undefined) => {
            if (card == null) {
                return;
            }

            if (excludeStarterCards && this.isStarterCard(card.name)) {
                return;
            }

            if (!seenNames.has(card.name)) {
                seenNames.add(card.name);
                orderedNames.push(card.name);
            }
        };

        const decks = [
            this.deckActionCardActive,
            this.deckActionCardDraw,
            this.deckActionCardDiscard,
            this.deckActionDrawLeft,
            this.deckActionDrawRight
        ];

        for (const deck of decks) {
            for (const card of deck.cards) {
                pushCardName(card);
            }
        }

        return orderedNames;
    }

    public restoreSerializedState(state: SerializedDeckState): void {
        this.resetDecks();
        this.deckActionCardDraw = this.loadCardsIntoDeck(this.deckActionCardDraw, state.deckActionCardDraw.cards);
        this.deckActionCardDiscard = this.loadCardsIntoDeck(this.deckActionCardDiscard, state.deckActionCardDiscard.cards);
        this.deckActionDrawLeft = this.loadCardsIntoDeck(this.deckActionDrawLeft, state.deckActionDrawLeft.cards);
        this.deckActionDrawRight = this.loadCardsIntoDeck(this.deckActionDrawRight, state.deckActionDrawRight.cards);
        this.deckActionCardActive = this.loadCardsIntoDeck(this.deckActionCardActive, state.deckActionCardActive.cards);

        this.objectiveOne = state.objectiveOne ? this.createCardByName(state.objectiveOne.name) ?? undefined : undefined;
        this.objectiveTwo = state.objectiveTwo ? this.createCardByName(state.objectiveTwo.name) ?? undefined : undefined;
        this.objectiveThree = state.objectiveThree ? this.createCardByName(state.objectiveThree.name) ?? undefined : undefined;
    }

    private resetDecks(): void {
        this.deckActionCardDraw = new Deck();
        this.deckActionCardDiscard = new Deck();
        this.deckActionDrawLeft = new Deck();
        this.deckActionDrawRight = new Deck();
        this.deckActionCardActive = new Deck();
        this.objectiveOne = undefined;
        this.objectiveTwo = undefined;
        this.objectiveThree = undefined;
    }

    private setupObjectives(currentSeed?: string, campaignGameNumber?: 1 | 2 | 3): void {
        const s1ObjectiveDeck = this.createDeck(objectivesS1Images);
        const s2ObjectiveDeck = this.createDeck(objectivesS2Images);
        const s3ObjectiveDeck = this.createDeck(objectiveS3Images);

        s1ObjectiveDeck.shuffle();
        s2ObjectiveDeck.shuffle();
        s3ObjectiveDeck.shuffle();

        this.objectiveOne = s1ObjectiveDeck.cards[0];
        this.objectiveTwo = s2ObjectiveDeck.cards[0];
        this.objectiveThree = s3ObjectiveDeck.cards[0];

        const seedEvent = getSeedEventDefinition(currentSeed, campaignGameNumber);

        if (seedEvent != null) {
            this.objectiveOne = new Card(seedEvent.seasonEndCards[1].name, seedEvent.seasonEndCards[1].imageUrl);
            this.objectiveTwo = new Card(seedEvent.seasonEndCards[2].name, seedEvent.seasonEndCards[2].imageUrl);
            this.objectiveThree = new Card(seedEvent.seasonEndCards[3].name, seedEvent.seasonEndCards[3].imageUrl);
        }
    }

    private setupStandardDecks(options: { hiveExpansionEnabled?: boolean; includeAdvancedCards: boolean; currentSeed?: string }): void {
        const starterActionDeck = this.createDeck(actionStarterImages);
        starterActionDeck.shuffle();

        const seasonOneBaseImages = options.hiveExpansionEnabled
            ? actionS1BaseImages.concat(hiveActionS1Images)
            : actionS1BaseImages;
        const seasonTwoBaseImages = options.hiveExpansionEnabled
            ? actionS2BaseImages.concat(hiveActionS2Images)
            : actionS2BaseImages;
        const seasonThreeBaseImages = options.hiveExpansionEnabled
            ? actionS3BaseImages.concat(hiveActionS3Images)
            : actionS3BaseImages;

        const actionS1BaseDeck = this.createDeck(seasonOneBaseImages);
        const actionS1FlexDeck = options.includeAdvancedCards ? this.createDeck(actionS1FlexImages) : new Deck();
        const actionS2BaseDeck = this.createDeck(seasonTwoBaseImages);
        const actionS2FlexDeck = options.includeAdvancedCards ? this.createDeck(actionS2FlexImages) : new Deck();
        const actionS3BaseDeck = this.createDeck(seasonThreeBaseImages);
        const actionS3FlexDeck = options.includeAdvancedCards ? this.createDeck(actionS3FlexImages) : new Deck();

        actionS1BaseDeck.shuffle();
        actionS2BaseDeck.shuffle();
        actionS3BaseDeck.shuffle();

        if (options.includeAdvancedCards) {
            actionS1FlexDeck.shuffle();
            actionS2FlexDeck.shuffle();
            actionS3FlexDeck.shuffle();
        }

        const s1ActionCardSelectionDeck = new Deck();
        for (let index = 0; index < 8; index++) {
            s1ActionCardSelectionDeck.addCard(actionS1BaseDeck.takeTopCard());
        }

        if (options.includeAdvancedCards) {
            for (let index = 0; index < 6; index++) {
                s1ActionCardSelectionDeck.addCard(actionS1FlexDeck.takeTopCard());
            }
        }
        s1ActionCardSelectionDeck.shuffle();

        const s2ActionCardSelectionDeck = new Deck();
        for (let index = 0; index < 8; index++) {
            s2ActionCardSelectionDeck.addCard(actionS2BaseDeck.takeTopCard());
        }

        if (options.includeAdvancedCards) {
            for (let index = 0; index < 5; index++) {
                s2ActionCardSelectionDeck.addCard(actionS2FlexDeck.takeTopCard());
            }
        }
        s2ActionCardSelectionDeck.shuffle();

        const s3ActionCardSelectionDeck = new Deck();
        for (let index = 0; index < 8; index++) {
            s3ActionCardSelectionDeck.addCard(actionS3BaseDeck.takeTopCard());
        }

        if (options.includeAdvancedCards) {
            for (let index = 0; index < 5; index++) {
                s3ActionCardSelectionDeck.addCard(actionS3FlexDeck.takeTopCard());
            }
        }
        s3ActionCardSelectionDeck.shuffle();

        this.dealSeasonCardsToRoundDecks(s3ActionCardSelectionDeck, 3);
        this.dealSeasonCardsToRoundDecks(s2ActionCardSelectionDeck, 4);
        this.dealSeasonCardsToRoundDecks(s1ActionCardSelectionDeck, 4);

        for (let index = 0; index < 9; index++) {
            this.deckActionCardDraw.addCard(starterActionDeck.takeTopCard());
        }

        for (let index = 0; index < 5; index++) {
            this.deckActionCardActive.addCard(this.deckActionCardDraw.takeTopCard());
        }
    }

    private setupCampaignDecks(options: { includeAdvancedCards: boolean; guaranteedActionCardNames: string[]; cellarExpansionEnabled?: boolean; currentSeed?: string }): void {
        const starterActionDeck = this.createDeck(actionStarterImages);
        starterActionDeck.shuffle();

        const seasonOneCards = this.buildCampaignSeasonDeck(1, options);
        const seasonTwoCards = this.buildCampaignSeasonDeck(2, options);
        const seasonThreeCards = this.buildCampaignSeasonDeck(3, options);

        this.dealSeasonCardsToRoundDecks(seasonThreeCards, 3);
        this.dealSeasonCardsToRoundDecks(seasonTwoCards, 4);
        this.dealSeasonCardsToRoundDecks(seasonOneCards, 4);

        for (let index = 0; index < 9; index++) {
            this.deckActionCardDraw.addCard(starterActionDeck.takeTopCard());
        }

        for (let index = 0; index < 5; index++) {
            this.deckActionCardActive.addCard(this.deckActionCardDraw.takeTopCard());
        }
    }

    private buildCampaignSeasonDeck(season: 1 | 2 | 3, options: { includeAdvancedCards: boolean; guaranteedActionCardNames: string[]; cellarExpansionEnabled?: boolean; currentSeed?: string }): Deck {
        const seasonBaseImages = season === 1
            ? actionS1BaseImages
            : season === 2
                ? actionS2BaseImages
                : actionS3BaseImages;
        const seasonAdvancedImages = season === 1
            ? actionS1FlexImages
            : season === 2
                ? actionS2FlexImages
                : actionS3FlexImages;
        const seasonCellarImages = season === 1
            ? cellarActionS1Images
            : season === 2
                ? cellarActionS2Images
                : cellarActionS3Images;

        const eligibleImages = seasonBaseImages
            .concat(options.cellarExpansionEnabled ? seasonCellarImages : [])
            .concat(options.includeAdvancedCards ? seasonAdvancedImages : []);
        const eligibleDeck = this.createDeck(eligibleImages);
        const guaranteedForSeason = options.guaranteedActionCardNames
            .map((cardName) => this.createCardByName(cardName))
            .filter((card): card is Card => card != null && this.getSeasonForCard(card.name) === season);
        const guaranteedNames = new Set(guaranteedForSeason.map((card) => card.name));
        const availableCards = eligibleDeck.cards.filter((card) => !guaranteedNames.has(card.name));
        const visibleCardCount = SEASON_VISIBLE_CARD_COUNTS[season];

        if (guaranteedForSeason.length > visibleCardCount) {
            throw new Error(`Too many guaranteed Season ${season} cards selected for this game.`);
        }

        const fillDeck = new Deck();
        for (const card of availableCards) {
            fillDeck.addCard(card);
        }
        fillDeck.shuffle();

        const finalDeck = new Deck();
        for (const guaranteedCard of guaranteedForSeason) {
            finalDeck.addCard(guaranteedCard);
        }

        while (finalDeck.getCardCount() < visibleCardCount) {
            finalDeck.addCard(fillDeck.takeTopCard());
        }

        finalDeck.shuffle();
        return finalDeck;
    }

    private dealSeasonCardsToRoundDecks(seasonDeck: Deck, cardsPerSide: number): void {
        for (let index = 0; index < cardsPerSide; index++) {
            this.deckActionDrawLeft.addCard(seasonDeck.takeTopCard());
            this.deckActionDrawRight.addCard(seasonDeck.takeTopCard());
        }
    }

    public ShowCurrentCards(): void {
        this.changeCardImage("objective1", this.objectiveOne?.imageUrl, false);

        this.changeCardImage("objective2", this.objectiveTwo?.imageUrl, false);

        this.changeCardImage("objective3", this.objectiveThree?.imageUrl, false);

        if (this.deckActionDrawLeft.getCardCount() > 0) {
            this.changeCardImage("nextCardLeft", this.deckActionDrawLeft.viewTopCard()?.imageUrl, true);
        } else {
            this.changeCardImage("nextCardLeft", undefined, true);
        }

        if (this.deckActionDrawRight.getCardCount() > 0) {
            this.changeCardImage("nextCardRight", this.deckActionDrawRight.viewTopCard()?.imageUrl, true);
        } else {
            this.changeCardImage("nextCardRight", undefined, true);
        }


        this.changeCardImage("activeCard1", this.deckActionCardActive.cards[0].imageUrl, true);
        this.changeCardImage("activeCard2", this.deckActionCardActive.cards[1].imageUrl, true);
        this.changeCardImage("activeCard3", this.deckActionCardActive.cards[2].imageUrl, true);
        this.changeCardImage("activeCard4", this.deckActionCardActive.cards[3].imageUrl, true);
        this.changeCardImage("activeCard5", this.deckActionCardActive.cards[4].imageUrl, true);

    }

    public ShowScoringObjective(season: number): void {
        const overlay = document.getElementById('scoring-objective-overlay');
        const title = document.getElementById('scoring-objective-title');
        const card = document.getElementById('scoring-objective-card');
        const objective = this.getObjectiveForSeason(season);

        if (overlay == null || title == null || card == null || objective == null) {
            this.HideScoringObjective();
            return;
        }

        title.textContent = `Score Season ${season} Objective`;
        card.style.backgroundImage = `url(${objective.imageUrl})`;
        overlay.classList.add('show');
        overlay.setAttribute('aria-hidden', 'false');
    }

    public HideScoringObjective(): void {
        const overlay = document.getElementById('scoring-objective-overlay');

        if (overlay == null) {
            return;
        }

        overlay.classList.remove('show');
        overlay.setAttribute('aria-hidden', 'true');
    }

    public SetupNextRound(): void {

        console.log('setting up next round');
        const cardCountOfActiveCards = this.deckActionCardActive.getCardCount();
        // first move all active cards to the discard
        for (let index = 0; index < cardCountOfActiveCards; index++) {
            this.deckActionCardDiscard.addCard(this.deckActionCardActive.takeTopCard());
            console.log('discarding card ' + index);
        }

        // Draw 3 cards from the deck
        for (let index = 0; index < 3; index++) {

            // If the draw pile is empty, transfer all cards from the discard pile to the draw pile and shuffle
            if (this.deckActionCardDraw.getCardCount() == 0) {
                console.log("Refreshing draw pile!");
                const discardPileSize = this.deckActionCardDiscard.getCardCount();
                for (let index = 0; index < discardPileSize; index++) {
                    this.deckActionCardDraw.addCard(this.deckActionCardDiscard.takeTopCard());
                }
            }

            this.deckActionCardActive.addCard(this.deckActionCardDraw.takeTopCard());
        }

        // Then add the new cards to the pile
        if (this.deckActionDrawLeft.getCardCount() > 0) {
            this.deckActionCardActive.addCard(this.deckActionDrawLeft.takeTopCard());
        }

        if (this.deckActionDrawRight.getCardCount() > 0) {
            this.deckActionCardActive.addCard(this.deckActionDrawRight.takeTopCard());
        }

    }

    private createDeck(images: string[]): Deck {
        const deck = new Deck();

        for (let index = 0; index < images.length; index++) {
            const url = images[index];
            const fileName = url.split('/').pop()?.split('.')[0];

            if (fileName != undefined) {

                const card = new Card(fileName, images[index]);
                //console.log('creating card: ' + card.name);
                deck.addCard(card);
            }
        }
        console.log(deck);
        return deck;
    }

    private createCardByName(cardName: string): Card | null {
        const actionCard = getActionCardDefinition(cardName);

        if (actionCard != null) {
            return new Card(actionCard.name, actionCard.imageUrl);
        }

        const seedEventCard = getSeedEventCardDefinition(cardName);
        if (seedEventCard != null) {
            return new Card(seedEventCard.name, seedEventCard.imageUrl);
        }

        const counterpart = findCardInImageArrays(cardName);

        if (counterpart != null) {
            return new Card(cardName, counterpart);
        }

        return null;
    }

    private getSeasonForCard(cardName: string): 1 | 2 | 3 | null {
        const actionCard = getActionCardDefinition(cardName);
        return actionCard?.season ?? null;
    }

    private isStarterCard(cardName: string): boolean {
        const actionCard = getActionCardDefinition(cardName);
        return actionCard?.source === "starter";
    }

    private getObjectiveForSeason(season: number): Card | undefined {
        if (season === 1) {
            return this.objectiveOne;
        }

        if (season === 2) {
            return this.objectiveTwo;
        }

        if (season === 3) {
            return this.objectiveThree;
        }

        return undefined;
    }

    

    private changeCardImage(cardId: string, imageUrl: string | undefined, animate: boolean | null): void {

        const card = document.getElementById(cardId);
        if (card) {
            const fullscreenContainer = document.getElementById('fullscreen-container');
            const fullscreenCard = document.getElementById('fullscreen-card');

            if (fullscreenCard != null && fullscreenContainer != null) {
                if (!this.cardsWithFullscreenListeners.has(cardId)) {
                    card.addEventListener('click', () => {
                        fullscreenCard.innerHTML = card.innerHTML;
                        fullscreenCard.style.backgroundImage = card.style.backgroundImage;
                        fullscreenCard.style.backgroundSize = 'cover';
                        fullscreenCard.style.backgroundPosition = 'center';
                        fullscreenContainer.classList.add('show');
                    });

                    this.cardsWithFullscreenListeners.add(cardId);
                }

                if (!this.fullscreenCloseListenerBound) {
                    fullscreenContainer.addEventListener('click', (e) => {
                        if (e.target === fullscreenContainer) {
                            fullscreenContainer.classList.remove('show');
                        }
                    });

                    this.fullscreenCloseListenerBound = true;
                }

            }

            if (animate == true) {
                card.classList.add('bouncy');

        // Remove the bouncy class after the animation ends to allow it to be retriggered
                setTimeout(() => {
                    card.classList.remove('bouncy');
                }, 600);
            }


            if (imageUrl == undefined) {
                imageUrl = "https://ucarecdn.com/a5791dbc-b180-47fd-aa98-7b7cad74ec91/-/preview/714x1000/"
            }
            card.style.backgroundImage = `url(${imageUrl})`;
            card.style.backgroundSize = 'cover';  // Ensures the image covers the entire card
            card.style.backgroundPosition = 'center';  // Centers the image
        } else {
            console.error(`Card with ID ${cardId} not found.`);
        }
    }

    saveStateToFile(filename: string) {
        const state = this.getSerializedState();

        fs.writeFileSync(filename, JSON.stringify(state, null, 2));
        console.log(`Deck state saved to ${filename}`);
    }

    saveStateToLocalStorage(keyword: string) {
        const state = this.getSerializedState();

        localStorage.setItem(keyword, JSON.stringify(state));
        console.log(`Deck state saved with keyword: ${keyword}`);
    }

    /*
    
    loadStateFromFile(filename: string) {
        const data = fs.readFileSync(filename, 'utf-8');
        const state = JSON.parse(data);

        this.deckActionCardDraw = Deck.deserialize(state.deckActionCardDraw);
        this.deckActionCardDiscard = Deck.deserialize(state.deckActionCardDiscard);
        this.deckActionDrawLeft = Deck.deserialize(state.deckActionDrawLeft);
        this.deckActionDrawRight = Deck.deserialize(state.deckActionDrawRight);
        this.deckActionCardActive = Deck.deserialize(state.deckActionCardActive);

        this.objectiveOne = state.objectiveOne ? Card.deserialize(state.objectiveOne) : undefined;
        this.objectiveTwo = state.objectiveTwo ? Card.deserialize(state.objectiveTwo) : undefined;
        this.objectiveThree = state.objectiveThree ? Card.deserialize(state.objectiveThree) : undefined;

        console.log(`Deck state loaded from ${filename}`);
    }
    */
    loadDeckFromFile(filePath: string) {
        fs.readFile(filePath, 'utf8', async (err, jsonString) => {
            if (err) {
                console.error("Error reading file:", err);
                return;
            }
            try {
                const data = JSON.parse(jsonString);

                // Assuming directory path is passed or known
                const directoryPath = './images'; // Example path

                const deckActionCardDraw = await Deck.deserialize(data.deckActionCardDraw, directoryPath);
                const deckActionCardDiscard = await Deck.deserialize(data.deckActionCardDiscard, directoryPath);
                const deckActionDrawLeft = await Deck.deserialize(data.deckActionDrawLeft, directoryPath);
                const deckActionDrawRight = await Deck.deserialize(data.deckActionDrawRight, directoryPath);
                const deckActionCardActive = await Deck.deserialize(data.deckActionCardActive, directoryPath);

                console.log("Decks loaded successfully:");
                console.log(deckActionCardDraw, deckActionCardDiscard, deckActionDrawLeft, deckActionDrawRight, deckActionCardActive);

            } catch (err) {
                console.error("Error parsing JSON string:", err);
            }
        });
    }

    async initializeDecks() {
        const directoryPath = './images';
        const deckActionCardDraw = await Deck.deserialize(deckData.deckActionCardDraw, directoryPath);
        const deckActionCardDiscard = await Deck.deserialize(deckData.deckActionCardDiscard, directoryPath);
        const deckActionDrawLeft = await Deck.deserialize(deckData.deckActionDrawLeft, directoryPath);
        const deckActionDrawRight = await Deck.deserialize(deckData.deckActionDrawRight, directoryPath);
        const deckActionCardActive = await Deck.deserialize(deckData.deckActionCardActive, directoryPath);

        console.log("Decks initialized successfully:");
        console.log(deckActionCardDraw, deckActionCardDiscard, deckActionDrawLeft, deckActionDrawRight, deckActionCardActive);
    }

}
